/**
 * useRealtimeSession — thin React hook around @openai/agents-realtime.
 *
 * Responsibilities:
 *   - fetch an ephemeral secret from our backend
 *   - construct a RealtimeAgent + RealtimeSession with a WebRTC transport
 *     whose <audio> element we own (so we can observe playback lifecycle)
 *   - drive the derived state machine (idle/connecting/listening/thinking/
 *     speaking/error)
 *   - collect per-turn diagnostics events so we can distinguish response
 *     generation, audio generation, playback, and interruption stages
 *   - expose transcript (raw + normalized via client-side glossary),
 *     mute/interrupt/end/sendText, and connection state
 *   - fire a one-shot greeting on genuinely-new conversations (not on any
 *     future internal reconnect)
 *
 * The hook never touches the OpenAI API key. It only ever sees the ek_...
 * ephemeral secret returned by the backend.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
    RealtimeAgent,
    RealtimeSession,
    OpenAIRealtimeWebRTC,
} from "@openai/agents-realtime";
import {
    createBackendSession,
    fetchGlossary,
    mintClientSecret,
    patchBackendSession,
    upsertSessionTurn,
} from "@/lib/api";
import { normalizeTranscript } from "@/utils/normalizeTranscript";
import { buildHistoricalLineageTool } from "@/hooks/historicalLineageTool";
import { acquireMicOrSilentFallback } from "@/hooks/micStream";
import {
    createInterruptionController,
    rearmPlayback,
    snapshotAudioPipeline,
} from "@/hooks/interruptionController";
import { classifyMintError } from "@/hooks/heldOffline";

// Cap the diagnostics buffer so a very long session doesn't grow memory
// unboundedly.
const DIAG_MAX = 400;

// How often we flush pending turn upserts to the backend. Keeps chatter
// off the WebRTC data channel critical path while still landing rows
// within a natural conversation cadence.
const TURN_FLUSH_DEBOUNCE_MS = 1500;

// If the initial POST /api/sessions fails, we retry ONCE after this
// delay. If the retry also fails we settle into "not_persisted" for the
// remainder of the session — the voice loop is never blocked.
const PERSISTENCE_RETRY_DELAY_MS = 5000;

// Small non-cryptographic hash over turn content so we can detect when
// a turn's text actually changed and only re-upsert the ones that did.
// (Sending on every history_updated would flood the API with no-ops
// during long assistant streams.)
function _turnHash(raw, normalized) {
    const s = `${raw ?? ""}\u0001${normalized ?? ""}`;
    let h = 5381;
    for (let i = 0; i < s.length; i += 1) {
        h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    }
    return h;
}

/**
 * Flatten SDK history into ordered transcript turns, applying client-side
 * normalization on top of raw ASR text. Raw is always preserved.
 *
 * Aborted-turn coalescing (Phase 1C acceptance-run finding, 2026-08-18):
 * ---------------------------------------------------------------------
 * Occasionally the OpenAI Realtime SDK emits TWO consecutive message
 * items with the same role for a single logical turn — a truncated /
 * cancelled response followed a few seconds later by a fresh full one.
 * Persisting both pollutes the transcript with fragmentary content
 * (e.g. ``"I'm not getting meaningful content from that. If you'r"``)
 * and confuses the summarizer.
 *
 * A well-formed conversation strictly alternates roles between founder
 * and assistant, so we treat any run of consecutive same-role message
 * items as a single logical turn and keep only the LAST item in the
 * run (its ``itemId`` and full text). This is safe because:
 *   • Normal SDK behaviour streams content deltas INTO the same item
 *     — it does not produce multiple items per turn.
 *   • The only observed producer of consecutive same-role items is
 *     the aborted-restart edge case, where the last item is the
 *     canonical full response.
 * We record a diagnostic line so the coalescing is visible in the
 * diagnostics panel and QA log.
 */
function flattenHistoryToTurns(history, glossary) {
    if (!Array.isArray(history)) return [];
    const turns = [];
    for (const item of history) {
        if (!item || item.type !== "message") continue;
        if (item.role !== "user" && item.role !== "assistant") continue;
        const parts = [];
        for (const c of item.content ?? []) {
            if (!c) continue;
            if (c.type === "input_text" && c.text) parts.push(c.text);
            else if (c.type === "output_text" && c.text) parts.push(c.text);
            else if (c.type === "input_audio" && c.transcript)
                parts.push(c.transcript);
            else if (c.type === "output_audio" && c.transcript)
                parts.push(c.transcript);
        }
        const raw = parts.join(" ").trim();
        if (!raw) continue;
        const { normalized, corrections } = normalizeTranscript(raw, glossary);
        // Aborted-turn coalescing: if the previous entry we accepted
        // is the SAME role, treat the earlier one as an aborted
        // partial and replace it with this newer, more complete item.
        // This keeps turn_index contiguous and the transcript honest.
        if (turns.length > 0 && turns[turns.length - 1].role === item.role) {
            const dropped = turns.pop();
            turns.push({
                id: item.itemId,
                role: item.role,
                raw,
                normalized,
                corrections,
                status: item.status ?? "completed",
                // Keep the coalesced-away item_id so the diagnostics
                // panel can show that a merge happened for this turn.
                coalescedFrom: dropped.id ?? null,
            });
            continue;
        }
        turns.push({
            id: item.itemId,
            role: item.role,
            raw,
            normalized,
            corrections,
            status: item.status ?? "completed",
        });
    }
    return turns;
}

export function useRealtimeSession({
    agentName = "Qoryn",
    projectId = null,
    persist = true,
} = {}) {
    const [agentState, setAgentState] = useState("idle");
    const [connectionState, setConnectionState] = useState("closed");
    const [turns, setTurns] = useState([]);
    const [error, setError] = useState(null);
    // Structured "paused" descriptor for the KNOWN backend hold signal
    // (HTTP 503 + detail.state === "REALTIME_HELD_OFFLINE_GATE_ACTIVE").
    // Non-null only for that exact classified case; any other failure
    // remains a real error via `error`. Never overloaded with generic
    // errors, never populated by substring matching. Cleared at the
    // top of each manual connect() so a stale paused flag cannot hide
    // a later 401 / unknown 503 / success.
    const [heldOffline, setHeldOffline] = useState(null);
    const [sessionMeta, setSessionMeta] = useState(null);
    const [micMuted, setMicMuted] = useState(false);
    // Text-only fallback mode. When the browser has no microphone
    // device or permission is denied, we still establish the WebRTC
    // session using a synthetic silent audio track so the Founder can
    // type into the live conversation. This flag lets the UI show a
    // small "text mode" indicator without redesigning the console.
    const [audioMode, setAudioMode] = useState("live"); // "live" | "text_only"
    const [audioModeReason, setAudioModeReason] = useState(null);
    const micCleanupRef = useRef(null);
    const [sessionStartedAt, setSessionStartedAt] = useState(null);
    const [diagnostics, setDiagnostics] = useState([]);
    const [glossary, setGlossary] = useState(null);

    // ---- Backend persistence state (Phase 1C wiring) ----------------------
    //
    // persistenceState machine:
    //   "idle"          — no active session, nothing to persist
    //   "creating"      — POST /api/sessions in flight
    //   "saving"        — session created, at least one turn upsert pending
    //   "saved"         — session created and all known turns flushed
    //   "not_persisted" — POST /api/sessions failed after retry OR user
    //                     is unauthenticated (backend returns 401). The
    //                     voice loop keeps running; UI shows an honest
    //                     "not persisted" banner. If a later background
    //                     retry succeeds, buffered turns are flushed and
    //                     the state flips to "saving" → "saved".
    //
    // These are truth-driven states: they only advance on actual API
    // responses, never optimistically.
    const [persistenceState, setPersistenceState] = useState("idle");
    const [persistedSessionId, setPersistedSessionId] = useState(null);
    const [persistenceError, setPersistenceError] = useState(null);

    const sessionRef = useRef(null);
    const agentRef = useRef(null);
    const audioElRef = useRef(null);
    const glossaryRef = useRef(null);
    const conversationIdRef = useRef(null);
    const greetedRef = useRef(false);
    // Tracks the current assistant response id so playback events know
    // which turn they belong to.
    const currentResponseRef = useRef(null);
    // Canonical Interruption Controller — single source of truth for
    // response.cancel + local playback mute + diagnostic emission,
    // used by voice barge-in, text submit, and any future trigger
    // (mobile tap, hardware button, agent-initiated yield). See
    // /app/frontend/src/hooks/interruptionController.js for the
    // canonical sequence.
    const interruptionCtrlRef = useRef(null);
    // Set once the audio element has fired its first "play" event for the
    // session, so we can distinguish "playback.started" from "resumed".
    const audioHasPlayedRef = useRef(false);

    // ---- Persistence refs -------------------------------------------------
    //
    // persistedSessionIdRef mirrors persistedSessionId so callbacks that
    // fire during teardown (e.g. end()) can read it synchronously without
    // relying on captured state. The React state variable drives UI; the
    // ref drives side-effects.
    const persistedSessionIdRef = useRef(null);
    // Map<turn_index, hash-of-content> — remembers the last-flushed hash
    // per turn so we only upsert turns whose content actually changed
    // since the last flush.
    const syncedHashesRef = useRef(new Map());
    // Latest turns snapshot captured in a ref so end() can flush without
    // racing React state updates that fire after teardown starts.
    const latestTurnsRef = useRef([]);
    // Latest project + voice + model we passed to createBackendSession —
    // needed if the retry path fires after refs have been cleared.
    const persistenceContextRef = useRef({ projectId, voice: null, model: null });
    // Timers we must clean up on end() so a stale flush doesn't fire
    // after the voice session is gone.
    const flushTimerRef = useRef(null);
    const retryTimerRef = useRef(null);
    // Guards against overlapping flushes if a debounce fires while the
    // previous flush's PUTs are still in flight.
    const flushInFlightRef = useRef(false);
    // Latch: once we've committed to "not_persisted" for this session we
    // stop the retry attempts (they'd never succeed if the user is
    // signed out mid-session, and we don't want unbounded background
    // retries). One retry is enough per the plan.
    const persistenceGaveUpRef = useRef(false);
    // Latch to ensure we only fire the final PATCH once per session.
    const finalizedRef = useRef(false);
    persistenceContextRef.current.projectId = projectId;

    // Audio-reactive plumbing. The orb reads from analyserRef inside its
    // own RAF loop so the visualizer stays provider-independent and there
    // are no re-renders per audio frame.
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const streamSourceRef = useRef(null);

    // Preload the glossary once. If it fails, normalization silently
    // becomes a no-op.
    useEffect(() => {
        let cancelled = false;
        fetchGlossary()
            .then((g) => {
                if (cancelled) return;
                glossaryRef.current = g;
                setGlossary(g);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    // Cleanup on unmount.
    useEffect(() => {
        return () => {
            try {
                sessionRef.current?.close?.();
            } catch (_) {
                /* noop */
            }
        };
    }, []);

    const pushDiag = useCallback((event) => {
        setDiagnostics((prev) => {
            const next = [
                ...prev,
                {
                    ts: Date.now(),
                    ...event,
                },
            ];
            return next.length > DIAG_MAX ? next.slice(-DIAG_MAX) : next;
        });
    }, []);

    // -----------------------------------------------------------------
    // Backend persistence — helpers.
    //
    // These are declared at the top level of the hook so both connect()
    // and the history_updated handler close over the same set. They are
    // deliberately fire-and-forget from the perspective of the WebRTC
    // audio loop; nothing here is ever awaited on the critical path.
    // -----------------------------------------------------------------

    /**
     * Flush every turn whose content hash differs from the last-flushed
     * hash to the backend via PUT /turns/{turn_index}. Skips flush if
     * we have no session id yet (buffered mode) or a flush is already
     * in flight. Safe to call more often than we mean to.
     */
    const flushTurns = useCallback(async () => {
        if (flushInFlightRef.current) return;
        const sid = persistedSessionIdRef.current;
        if (!sid) return;
        const snapshot = latestTurnsRef.current;
        if (!snapshot || snapshot.length === 0) return;
        flushInFlightRef.current = true;
        setPersistenceState((prev) =>
            prev === "not_persisted" ? prev : "saving",
        );
        const dirty = [];
        snapshot.forEach((t, ix) => {
            const h = _turnHash(t.raw, t.normalized);
            const prev = syncedHashesRef.current.get(ix);
            if (prev !== h) dirty.push({ ix, t, h });
        });
        try {
            for (const { ix, t, h } of dirty) {
                const speaker = t.role === "assistant" ? "qoryn" : "founder";
                await upsertSessionTurn(sid, ix, {
                    speaker,
                    raw_text: t.raw ?? null,
                    normalized_text: t.normalized ?? null,
                    metadata: {
                        item_id: t.id ?? null,
                        sdk_status: t.status ?? null,
                        // Non-null only when this turn was formed by
                        // coalescing an aborted same-role predecessor
                        // (see flattenHistoryToTurns). Ops can grep the
                        // metadata JSONB to spot when the SDK produced
                        // duplicate items for a single logical turn.
                        coalesced_from_item_id: t.coalescedFrom ?? null,
                    },
                });
                syncedHashesRef.current.set(ix, h);
                pushDiag({
                    type: "persistence.turn_upserted",
                    detail: {
                        turn_index: ix,
                        speaker,
                        coalesced: t.coalescedFrom ? true : false,
                    },
                });
            }
            // Only flip to "saved" if the just-observed snapshot is
            // still the latest one AND nothing else marked us dirty
            // during flush. If new turns landed while we were flushing
            // they'll re-schedule the debounced flush themselves.
            setPersistenceState((prev) => {
                if (prev === "not_persisted") return prev;
                // Re-check dirtiness against the (possibly newer) latest
                // snapshot so we don't lie about being "saved".
                const now = latestTurnsRef.current;
                const stillDirty = now.some((t, ix) => {
                    const h = _turnHash(t.raw, t.normalized);
                    return syncedHashesRef.current.get(ix) !== h;
                });
                return stillDirty ? "saving" : "saved";
            });
        } catch (e) {
            // Best-effort: log and try again on the next debounce cycle.
            // We do NOT flip to "not_persisted" here — a transient PUT
            // failure shouldn't lie to the founder about the whole
            // session. Only session-create failure moves us there.
            console.warn("[Qoryn] turn flush failed:", e);
            pushDiag({
                type: "persistence.turn_flush_failed",
                detail: {
                    status: e?.status ?? null,
                    message: e?.message ?? String(e),
                },
            });
        } finally {
            flushInFlightRef.current = false;
        }
    }, [pushDiag]);

    /** Debounce wrapper — called from history_updated handler. */
    const scheduleTurnFlush = useCallback(() => {
        if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
        flushTimerRef.current = setTimeout(
            () => {
                flushTimerRef.current = null;
                flushTurns();
            },
            TURN_FLUSH_DEBOUNCE_MS,
        );
    }, [flushTurns]);

    /**
     * Kick off POST /api/sessions. On success stores the id and flips
     * to "saving" (or "saved" if turns is empty). On failure schedules
     * a single retry; if the retry also fails settles into
     * "not_persisted" but keeps the buffered turns so a future manual
     * retry could still recover them.
     */
    const createBackendSessionWithRetry = useCallback(async () => {
        if (!persist) {
            setPersistenceState("idle");
            return;
        }
        const ctx = persistenceContextRef.current;
        setPersistenceState("creating");
        setPersistenceError(null);
        try {
            const row = await createBackendSession({
                project_id: ctx.projectId ?? null,
                voice: ctx.voice ?? null,
                model: ctx.model ?? null,
            });
            persistedSessionIdRef.current = row.id;
            setPersistedSessionId(row.id);
            pushDiag({
                type: "persistence.session_created",
                detail: { session_id: row.id, project_id: row.project_id },
            });
            // A successful late-create might discover buffered turns
            // from the "not_persisted" window: run one immediate flush
            // so they land instead of waiting for the next SDK event.
            setPersistenceState(latestTurnsRef.current.length ? "saving" : "saved");
            scheduleTurnFlush();
        } catch (e) {
            const status = e?.status ?? null;
            pushDiag({
                type: "persistence.session_create_failed",
                detail: { status, message: e?.message ?? String(e) },
            });
            // 401 means the user isn't signed in — there's no point
            // retrying. Any other error may be transient; retry once.
            if (status === 401 || persistenceGaveUpRef.current) {
                persistenceGaveUpRef.current = true;
                setPersistenceError(
                    status === 401
                        ? "Sign in to persist conversations"
                        : (e?.message ?? "Persistence unavailable"),
                );
                setPersistenceState("not_persisted");
                return;
            }
            // Schedule a single retry. We DO NOT flip to
            // "not_persisted" yet — the banner would be a lie for the
            // first 5s while the retry is genuinely still pending.
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
            retryTimerRef.current = setTimeout(() => {
                retryTimerRef.current = null;
                persistenceGaveUpRef.current = true; // next fail is terminal
                createBackendSessionWithRetry();
            }, PERSISTENCE_RETRY_DELAY_MS);
        }
    }, [persist, pushDiag, scheduleTurnFlush]);

    /**
     * Finalize the DB session — flush any pending turns, then PATCH
     * status → ended (which auto-enqueues session.summarize.v1
     * server-side). Called from end() BEFORE state teardown so the
     * captured refs are still valid. Fire-and-forget: nothing waits on
     * the returned promise; the console teardown is independent.
     */
    const finalizePersistence = useCallback(async (durationSeconds) => {
        if (finalizedRef.current) return;
        finalizedRef.current = true;
        const sid = persistedSessionIdRef.current;
        if (!sid) {
            // Nothing to finalize — session was never created (offline,
            // unauthenticated, or POST /sessions failed permanently).
            pushDiag({
                type: "persistence.finalize_skipped",
                detail: { reason: "no_session_id" },
            });
            return;
        }
        // Cancel any pending debounced flush so we don't double-fire.
        if (flushTimerRef.current) {
            clearTimeout(flushTimerRef.current);
            flushTimerRef.current = null;
        }
        try {
            await flushTurns();
        } catch (e) {
            console.warn("[Qoryn] final flush failed:", e);
        }
        try {
            await patchBackendSession(sid, {
                status: "ended",
                duration_seconds: Math.max(0, Math.floor(durationSeconds ?? 0)),
            });
            pushDiag({
                type: "persistence.session_ended",
                detail: { session_id: sid, duration_seconds: durationSeconds },
            });
        } catch (e) {
            console.warn("[Qoryn] session PATCH ended failed:", e);
            pushDiag({
                type: "persistence.session_end_failed",
                detail: {
                    session_id: sid,
                    status: e?.status ?? null,
                    message: e?.message ?? String(e),
                },
            });
        }
    }, [flushTurns, pushDiag]);

    const attachConnectionTracking = useCallback(
        (session) => {
            const transport = session?.transport;
            if (!transport) return;
            if (transport.status) setConnectionState(transport.status);
            try {
                transport.on("connection_change", (status) => {
                    setConnectionState(status);
                    pushDiag({
                        type: "connection",
                        detail: { status },
                    });
                });
            } catch (e) {
                console.warn("[Qoryn] connection_change attach failed:", e);
            }
        },
        [pushDiag],
    );

    const attachTransportDiagnostics = useCallback(
        (session) => {
            const transport = session?.transport;
            if (!transport) return;
            try {
                transport.on("*", () => {}); // ensure event bus is live
            } catch (_) {
                /* noop */
            }
            // Individual raw events we care about for the lifecycle.
            const wire = (event, mapper) => {
                try {
                    transport.on(event, (payload) =>
                        pushDiag(mapper(payload)),
                    );
                } catch (_) {
                    /* noop */
                }
            };
            wire("response.created", (p) => {
                const rid = p?.response?.id ?? null;
                const prevRid = currentResponseRef.current;
                currentResponseRef.current = rid;
                // Multi-signal release, signal #1 — new response id
                // observed. If we muted the audio element via the
                // canonical interruption controller (voice barge-in,
                // text submit, etc.), restore the user's saved
                // speaker preference now. `releaseMuteForNewResponse`
                // also runs the `rearmPlayback` watchdog defensively,
                // so a paused pipeline is unpaused even when no
                // interrupt was in flight.
                try {
                    interruptionCtrlRef.current?.releaseMuteForNewResponse(
                        rid,
                        {
                            onlyIfDifferent: prevRid,
                            source: "response.created",
                        },
                    );
                } catch (_) {
                    /* noop */
                }
                // Persistent diagnostic — Founder-visible audibility
                // breadcrumb at every response boundary. Cheap to
                // capture; invaluable when debugging live regressions.
                try {
                    pushDiag({
                        type: "audio.pipeline_snapshot",
                        response_id: rid,
                        detail: {
                            at: "response.created",
                            pipeline: snapshotAudioPipeline(
                                audioElRef.current,
                                audioCtxRef.current,
                            ),
                        },
                    });
                } catch (_) {
                    /* noop */
                }
                return {
                    type: "response.generated",
                    response_id: rid,
                    detail: {},
                };
            });
            wire("response.output_audio.done", () => ({
                type: "audio.generation_completed",
                response_id: currentResponseRef.current,
                detail: {},
            }));
            wire("response.done", (p) => ({
                type: "response.completed",
                response_id: p?.response?.id ?? currentResponseRef.current,
                detail: {
                    status: p?.response?.status ?? "unknown",
                },
            }));
            wire("response.error", (p) => ({
                type: "response.error",
                response_id: currentResponseRef.current,
                detail: p?.error ?? {},
            }));
            wire("session.updated", (p) => {
                const applied = p?.session?.audio?.input?.turn_detection
                    ?? p?.session?.turn_detection ?? null;
                return {
                    type: "session.updated",
                    detail: { applied_turn_detection: applied },
                };
            });
        },
        [pushDiag],
    );

    /**
     * Attach playback lifecycle listeners to the audio element we own.
     * Runs once per session; the audio element is created before connect.
     */
    const attachAudioElementDiagnostics = useCallback(
        (audio) => {
            if (!audio) return;
            const on = (evt, handler) => {
                audio.addEventListener(evt, handler);
            };
            const emit = (type) =>
                pushDiag({
                    type,
                    response_id: currentResponseRef.current,
                    detail: {
                        paused: audio.paused,
                        muted: audio.muted,
                        currentTime: audio.currentTime,
                        readyState: audio.readyState,
                    },
                });
            on("play", () => {
                if (!audioHasPlayedRef.current) {
                    audioHasPlayedRef.current = true;
                    emit("playback.started");
                } else {
                    emit("playback.resumed");
                }
                // Wire the Qoryn output stream into a Web Audio analyser
                // the first time the audio element actually starts playing.
                // The SDK sets audio.srcObject to the remote MediaStream on
                // connect; createMediaStreamSource observes that stream
                // WITHOUT diverting playback (the audio element still
                // handles output).
                try {
                    const stream = audio.srcObject;
                    if (
                        stream instanceof MediaStream &&
                        !analyserRef.current
                    ) {
                        const Ctx =
                            window.AudioContext || window.webkitAudioContext;
                        if (Ctx) {
                            const ctx = new Ctx();
                            const source =
                                ctx.createMediaStreamSource(stream);
                            const analyser = ctx.createAnalyser();
                            analyser.fftSize = 512;
                            analyser.smoothingTimeConstant = 0.72;
                            source.connect(analyser);
                            // NOTE: do NOT connect analyser to
                            // ctx.destination — the audio element already
                            // renders playback. Connecting would double-play.
                            audioCtxRef.current = ctx;
                            streamSourceRef.current = source;
                            analyserRef.current = analyser;
                            pushDiag({
                                type: "audio.analyser_attached",
                                detail: {
                                    fftSize: analyser.fftSize,
                                    sampleRate: ctx.sampleRate,
                                },
                            });
                        }
                    }
                } catch (e) {
                    console.warn("[Qoryn] analyser attach failed:", e);
                }
            });
            on("playing", () => emit("playback.playing"));
            on("pause", () => emit("playback.paused"));
            on("ended", () => emit("playback.completed"));
            on("waiting", () => emit("playback.buffering"));
            on("stalled", () => emit("playback.stalled"));
            on("error", () => emit("playback.error"));
        },
        [pushDiag],
    );

    const maybeFireGreeting = useCallback(
        (session, greetingCfg) => {
            if (!greetingCfg?.enabled || !greetingCfg?.text) return;
            if (greetedRef.current) return;
            const transport = session?.transport;
            if (!transport) return;
            const instructions = `Open the conversation. Say, in your own voice and warmly, exactly this and nothing more: "${greetingCfg.text}"`;
            try {
                if (typeof transport.requestResponse === "function") {
                    transport.requestResponse({ instructions });
                } else if (typeof transport.sendEvent === "function") {
                    transport.sendEvent({
                        type: "response.create",
                        response: { instructions },
                    });
                } else {
                    return;
                }
                greetedRef.current = true;
                pushDiag({
                    type: "greeting.triggered",
                    detail: { text: greetingCfg.text },
                });
            } catch (e) {
                console.warn("[Qoryn] greeting trigger failed:", e);
            }
        },
        [pushDiag],
    );

    const connect = useCallback(
        async ({ voice } = {}) => {
            if (agentState !== "idle" && agentState !== "error") return;
            setError(null);
            // Clear any prior held-offline paused flag: this manual
            // attempt's actual outcome is the only source of truth.
            setHeldOffline(null);
            setTurns([]);
            setDiagnostics([]);
            setAgentState("connecting");
            setConnectionState("new");

            conversationIdRef.current =
                globalThis.crypto?.randomUUID?.() ?? `c_${Date.now()}`;
            greetedRef.current = false;
            currentResponseRef.current = null;
            audioHasPlayedRef.current = false;

            // Reset persistence machine for the new session.
            persistedSessionIdRef.current = null;
            setPersistedSessionId(null);
            syncedHashesRef.current = new Map();
            latestTurnsRef.current = [];
            flushInFlightRef.current = false;
            persistenceGaveUpRef.current = false;
            finalizedRef.current = false;
            setPersistenceError(null);
            setPersistenceState(persist ? "idle" : "idle");
            if (flushTimerRef.current) {
                clearTimeout(flushTimerRef.current);
                flushTimerRef.current = null;
            }
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }

            pushDiag({
                type: "session.connect_requested",
                detail: { conversationId: conversationIdRef.current },
            });

            try {
                const secret = await mintClientSecret({ voice });
                pushDiag({
                    type: "session.secret_minted",
                    detail: {
                        model: secret.model,
                        voice: secret.voice,
                        turn_detection: secret.turn_detection,
                        expires_at: secret.expires_at,
                    },
                });

                // Own the <audio> element so we can observe playback state
                // directly. Attached to the DOM (hidden) so QA tooling can
                // find it via [data-testid=qoryn-audio-element] and verify
                // its `.muted` state when the top-right speaker button is
                // toggled. The SDK pipes the model's audio stream into it
                // and it plays regardless of visibility.
                const audio = document.createElement("audio");
                audio.autoplay = true;
                audio.setAttribute("data-testid", "qoryn-audio-element");
                audio.style.display = "none";
                document.body.appendChild(audio);
                audioElRef.current = audio;
                attachAudioElementDiagnostics(audio);

                // Acquire the mic BEFORE constructing the transport so
                // we can gracefully fall back to a silent MediaStream
                // when no mic device or permission is available. This
                // is what makes the text-input path work end-to-end
                // when there is no microphone — session.connect()
                // still succeeds because the WebRTC transport has a
                // valid (silent) audio track, and RealtimeSession
                // .sendMessage() rides the data channel independent of
                // the audio track.
                const mic = await acquireMicOrSilentFallback();
                micCleanupRef.current = mic.cleanup;
                setAudioMode(mic.mode);
                setAudioModeReason(mic.fallbackReason);
                pushDiag({
                    type: "session.mic_acquired",
                    detail: {
                        mode: mic.mode,
                        fallback_reason: mic.fallbackReason,
                    },
                });

                const transport = new OpenAIRealtimeWebRTC({
                    audioElement: audio,
                    mediaStream: mic.mediaStream,
                });

                const agent = new RealtimeAgent({
                    name: agentName,
                    // The backend composes the full system prompt from
                    // versioned assets + the live capability manifest and
                    // returns it here. The SDK sends session.update on
                    // connect using this exact string, so it MUST match
                    // what the server put into the ephemeral secret — any
                    // divergence means the SDK silently overrides the
                    // server-side instructions.
                    instructions:
                        secret.instructions ??
                        "You are Qoryn. System instructions unavailable — refuse to answer until reconnected.",
                    // Phase 1D closure extension — the historical
                    // supersession-lineage tool. Registered here (not at
                    // session-config in the mint payload) because the
                    // execute() handler needs to close over the JWT +
                    // active project id in browser JS. The tool is a
                    // pure additive registration and does NOT touch the
                    // verified voice/barge-in loop mechanics.
                    tools: [
                        buildHistoricalLineageTool({ projectId }),
                    ],
                });
                const session = new RealtimeSession(agent, {
                    model: secret.model,
                    transport,
                    // Mirror the server-side turn_detection under the new
                    // GA config path so the SDK's initial session.update
                    // doesn't clobber it back to the default server_vad.
                    config: {
                        audio: {
                            input: {
                                turnDetection:
                                    secret.turn_detection ?? undefined,
                            },
                        },
                    },
                });
                sessionRef.current = session;
                // Build the canonical Interruption Controller here —
                // it needs live refs to the session and the audio
                // element, and it lives for the lifetime of this
                // connect() call. Voice barge-in, text submit, and any
                // future trigger route through this one instance.
                interruptionCtrlRef.current = createInterruptionController({
                    getSession: () => sessionRef.current,
                    getAudioEl: () => audioElRef.current,
                    getAudioCtx: () => audioCtxRef.current,
                    pushDiag,
                });

                agentRef.current = agent;
                sessionRef.current = session;

                // Event wiring -------------------------------------------------
                session.on("history_updated", (history) => {
                    const flat = flattenHistoryToTurns(
                        history,
                        glossaryRef.current,
                    );
                    latestTurnsRef.current = flat;
                    setTurns(flat);
                    // Debounced upsert to the backend. Persistence hook
                    // no-ops if the session hasn't been created yet
                    // (buffered mode); the create flow will run one
                    // immediate flush the moment it lands.
                    if (persist) scheduleTurnFlush();
                });
                session.on("agent_start", () => {
                    setAgentState("thinking");
                    pushDiag({ type: "agent.start", detail: {} });
                });
                session.on("agent_end", () => {
                    setAgentState("listening");
                    pushDiag({ type: "agent.end", detail: {} });
                });
                session.on("audio_start", () => {
                    setAgentState("speaking");
                    // Multi-signal release, signal #2 — first audio
                    // frame observed. Ground-truth signal that "Qoryn
                    // is producing sound right now" — fires whether
                    // or not the SDK's response.created event has
                    // been observed yet on the data channel.
                    // Founder audibility watchdog (2026-08-18): if
                    // ANY interrupt is lingering OR the pipeline is
                    // paused, force-recover — the response must be
                    // audible even if the release path missed.
                    try {
                        interruptionCtrlRef.current?.releaseMuteForNewResponse(
                            currentResponseRef.current,
                            { source: "audio_start" },
                        );
                    } catch (_) {
                        /* noop */
                    }
                    try {
                        rearmPlayback({
                            audioEl: audioElRef.current,
                            audioCtx: audioCtxRef.current,
                            pushDiag,
                            reason: "audio_start_watchdog",
                        });
                    } catch (_) {
                        /* noop */
                    }
                    pushDiag({
                        type: "audio.generation_started",
                        response_id: currentResponseRef.current,
                        detail: {
                            pipeline: snapshotAudioPipeline(
                                audioElRef.current,
                                audioCtxRef.current,
                            ),
                        },
                    });
                });
                session.on("audio_stopped", () => {
                    setAgentState("listening");
                    pushDiag({
                        type: "audio.generation_stopped",
                        response_id: currentResponseRef.current,
                        detail: {},
                    });
                });
                session.on("audio_interrupted", () => {
                    setAgentState("listening");
                    // Voice barge-in — the SDK's internal VAD already
                    // invoked session.interrupt() before emitting
                    // this event. Route through the canonical
                    // controller for the client-side sequence (mute
                    // + diagnostic) so behavior is identical to
                    // text_submit / user_stop / future triggers.
                    // sdkAlreadyInterrupted:true prevents a
                    // double-fire of the primitive. The controller
                    // NEVER pauses the audio element (see
                    // interruptionController.js docstring for the
                    // 2026-08-18 audibility-regression RCA).
                    try {
                        interruptionCtrlRef.current?.interrupt({
                            reason: "voice_bargein",
                            sdkAlreadyInterrupted: true,
                            activeResponseId: currentResponseRef.current,
                            prevAgentState: "speaking",
                        });
                    } catch (_) {
                        /* noop */
                    }
                    pushDiag({
                        type: "audio.interrupted",
                        response_id: currentResponseRef.current,
                        detail: {
                            source: "user_barge_in",
                            pipeline: snapshotAudioPipeline(
                                audioElRef.current,
                                audioCtxRef.current,
                            ),
                        },
                    });
                });
                session.on("error", (evt) => {
                    console.error("[Qoryn] session error:", evt);
                    setError(
                        evt?.error?.message ??
                            String(evt?.error ?? "Unknown session error"),
                    );
                    setAgentState("error");
                    pushDiag({
                        type: "session.error",
                        detail: {
                            message:
                                evt?.error?.message ?? String(evt?.error ?? "?"),
                        },
                    });
                });

                attachTransportDiagnostics(session);

                await session.connect({ apiKey: secret.value });
                attachConnectionTracking(session);

                setSessionMeta({
                    model: secret.model,
                    voice: secret.voice,
                    turnDetection: secret.turn_detection,
                    expiresAt: secret.expires_at,
                });
                setSessionStartedAt(Date.now());
                setAgentState("listening");
                pushDiag({
                    type: "session.connected",
                    detail: {
                        model: secret.model,
                        voice: secret.voice,
                        turn_detection: secret.turn_detection,
                    },
                });

                // Kick off backend session creation for durable
                // persistence. Non-blocking: the WebRTC audio loop is
                // already live at this point; a failure here surfaces
                // a "not persisted" banner but never disturbs the call.
                if (persist) {
                    persistenceContextRef.current.voice = secret.voice ?? null;
                    persistenceContextRef.current.model = secret.model ?? null;
                    createBackendSessionWithRetry();
                }

                maybeFireGreeting(session, secret.greeting);
            } catch (e) {
                console.error("[Qoryn] connect failed:", e);
                // Classify the mint error against the ONLY held signal
                // (HTTP 503 + parsed detail.state === HELD_STATE_ID).
                // No substring matching. Any other failure — 401,
                // network, non-503, malformed JSON, or a 503 with a
                // different / missing detail.state — falls through to
                // the real error path below.
                const cls = classifyMintError(e);
                // Release any resources that were actually allocated
                // before the throw, using the same primitives the
                // canonical cleanup path (end()) uses. In the common
                // case (mintClientSecret is the first await), no
                // resources were created; these guards make the
                // cleanup safe when a failure lands later in the flow.
                try {
                    const a = audioElRef.current;
                    if (a) {
                        a.pause();
                        a.srcObject = null;
                        try { a.remove(); } catch (_) { /* noop */ }
                    }
                } catch (_) { /* noop */ }
                try {
                    sessionRef.current?.close?.();
                } catch (_) { /* noop */ }
                try {
                    streamSourceRef.current?.disconnect?.();
                    audioCtxRef.current?.close?.();
                } catch (_) { /* noop */ }
                try {
                    micCleanupRef.current?.();
                } catch (_) { /* noop */ }
                try {
                    interruptionCtrlRef.current?.reset();
                } catch (_) { /* noop */ }
                micCleanupRef.current = null;
                streamSourceRef.current = null;
                analyserRef.current = null;
                audioCtxRef.current = null;
                sessionRef.current = null;
                agentRef.current = null;
                audioElRef.current = null;
                interruptionCtrlRef.current = null;

                if (cls.kind === "held") {
                    // Neutral paused presentation — NOT an error visual
                    // state. `error` stays null so no red "SESSION
                    // ERROR" tile renders. Structured status carries
                    // through to the UI verbatim from the classifier.
                    setHeldOffline({
                        active: true,
                        state: cls.state,
                        headline: cls.headline,
                        supporting: cls.supporting,
                    });
                    setAgentState("idle");
                    setConnectionState("closed");
                    pushDiag({
                        type: "session.held_offline",
                        detail: { state: cls.state },
                    });
                    return;
                }

                const detail = e?.detail
                    ? JSON.stringify(e.detail).slice(0, 240)
                    : e?.message ?? String(e);
                setError(detail);
                setAgentState("error");
                setConnectionState("failed");
                pushDiag({
                    type: "session.connect_failed",
                    detail: { error: detail },
                });
            }
        },
        [
            agentState,
            agentName,
            persist,
            projectId,
            attachAudioElementDiagnostics,
            attachTransportDiagnostics,
            attachConnectionTracking,
            createBackendSessionWithRetry,
            maybeFireGreeting,
            pushDiag,
            scheduleTurnFlush,
        ],
    );

    const end = useCallback(() => {
        pushDiag({ type: "session.end_requested", detail: {} });
        // Compute duration BEFORE we clear sessionStartedAt below so the
        // finalize PATCH carries an accurate duration_seconds. If we
        // never started (e.g. connect failed), duration is 0.
        const durationSec = sessionStartedAt
            ? Math.max(0, Math.floor((Date.now() - sessionStartedAt) / 1000))
            : 0;
        // Fire-and-forget: finalize does its own error handling and
        // never throws to the caller. We deliberately do NOT await —
        // the teardown below must be synchronous so mic/stream release
        // happens the instant the user hits End.
        if (persist) finalizePersistence(durationSec);
        try {
            sessionRef.current?.close?.();
        } catch (_) {
            /* noop */
        }
        try {
            const a = audioElRef.current;
            if (a) {
                a.pause();
                a.srcObject = null;
                try { a.remove(); } catch (_) { /* noop */ }
            }
        } catch (_) {
            /* noop */
        }
        try {
            streamSourceRef.current?.disconnect?.();
            audioCtxRef.current?.close?.();
        } catch (_) {
            /* noop */
        }
        // Release the mic (real or synthetic silent stream) captured in
        // acquireMicOrSilentFallback so the OS mic indicator turns off
        // and the AudioContext for the silent-fallback path is closed.
        try {
            micCleanupRef.current?.();
        } catch (_) {
            /* noop */
        }
        micCleanupRef.current = null;
        if (flushTimerRef.current) {
            clearTimeout(flushTimerRef.current);
            flushTimerRef.current = null;
        }
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
        streamSourceRef.current = null;
        analyserRef.current = null;
        audioCtxRef.current = null;
        sessionRef.current = null;
        agentRef.current = null;
        audioElRef.current = null;
        conversationIdRef.current = null;
        greetedRef.current = false;
        currentResponseRef.current = null;
        audioHasPlayedRef.current = false;
        try {
            interruptionCtrlRef.current?.reset();
        } catch (_) {
            /* noop */
        }
        interruptionCtrlRef.current = null;
        setConnectionState("closed");
        setAgentState("idle");
        setSessionMeta(null);
        setSessionStartedAt(null);
        setMicMuted(false);
        setAudioMode("live");
        setAudioModeReason(null);
        // Note: we intentionally leave persistedSessionId set so the
        // console can link to the just-ended session's detail page in
        // the UI after End is pressed. It's reset on the next connect().
        setPersistenceState((prev) =>
            prev === "not_persisted" ? "not_persisted" : "idle",
        );
    }, [pushDiag, persist, sessionStartedAt, finalizePersistence]);

    const toggleMute = useCallback(() => {
        setMicMuted((prev) => {
            const next = !prev;
            try {
                sessionRef.current?.mute?.(next);
            } catch (_) {
                /* noop */
            }
            pushDiag({ type: "mic.muted", detail: { muted: next } });
            return next;
        });
    }, [pushDiag]);

    /**
     * Canonical user-initiated interrupt (future UI stop button,
     * keyboard shortcut, mobile tap, hardware button). Routes through
     * the same controller voice barge-in and text-submit use so the
     * behavior is byte-identical across all triggers.
     *
     * @param {{reason?: "user_stop" | "text_submit" | "voice_bargein" | "agent_yield" | "unknown", extra?: object}} [opts]
     */
    const interrupt = useCallback(
        (opts = {}) => {
            const reason = opts.reason || "user_stop";
            const activeResponseId = currentResponseRef.current;
            try {
                interruptionCtrlRef.current?.interrupt({
                    reason,
                    sdkAlreadyInterrupted: false,
                    activeResponseId,
                    prevAgentState: agentState,
                    extra: opts.extra || {},
                });
                if (activeResponseId) {
                    currentResponseRef.current = null;
                }
            } catch (_) {
                /* noop — controller is resilient but be defensive here */
            }
        },
        [agentState],
    );

    // Clear only the on-screen transcript buffer. Session (if live) stays
    // connected — this is a UI reset, not a session teardown. Used by the
    // QuickActionLauncher's "Clear transcript" action.
    const clearTurns = useCallback(() => {
        setTurns([]);
        pushDiag({ type: "transcript.cleared", detail: {} });
    }, [pushDiag]);

    const sendText = useCallback(
        (text) => {
            const trimmed = (text ?? "").trim();
            if (!trimmed) return;
            try {
                // Root cause of "typed input during active speech is
                // delayed 20-30s" (Founder-observed 2026-08-18): the
                // SDK's sendMessage() enqueues conversation.item.create
                // + response.create on the data channel, but the
                // OpenAI Realtime server won't start a new response
                // while one is active — it queues the new turn until
                // the current one completes.
                //
                // Fix: route through the canonical Interruption
                // Controller — the SAME controller voice barge-in
                // uses — so typed submits become first-class
                // interrupts. The controller: (a) invokes the SDK's
                // interrupt primitive (response.cancel +
                // output_audio_buffer.clear), (b) synchronously mutes
                // the audio element so already-buffered WebRTC audio
                // frames become inaudible immediately (~ms) WITHOUT
                // pausing the pipeline (pausing a MediaStream-backed
                // element breaks auto-resume — see 2026-08-18
                // audibility RCA in interruptionController.js), (c)
                // emits a session.interrupted diagnostic with
                // reason='text_submit', (d) preserves the user's
                // saved speaker preference for restore on the next
                // response.created / audio_start (multi-signal
                // release + rearmPlayback watchdog).
                const activeResponseId = currentResponseRef.current;
                // The guard for "should this text submit interrupt an
                // active response?" has to survive a race: the SDK's
                // response.created event (which sets
                // currentResponseRef.current) may fire hundreds of
                // milliseconds AFTER the audio pipeline has already
                // started emitting Qoryn's speech. If we only trust
                // currentResponseRef, a text-interrupt fired inside
                // that window gets silently downgraded to a plain
                // send and the pre-fix bug behavior returns for one
                // cycle. So we ALSO trust the audio pipeline itself
                // — if agentState says speaking/thinking OR the
                // audio element is unpaused (Qoryn is literally
                // making sound right now), we interrupt. The
                // controller accepts activeResponseId=null just fine.
                const audioEl = audioElRef.current;
                const audioIsPlaying = !!(audioEl && !audioEl.paused);
                const isActiveResponse =
                    !!activeResponseId
                    || agentState === "speaking"
                    || agentState === "thinking"
                    || audioIsPlaying;
                if (isActiveResponse) {
                    interruptionCtrlRef.current?.interrupt({
                        reason: "text_submit",
                        sdkAlreadyInterrupted: false,
                        activeResponseId,
                        prevAgentState: agentState,
                        extra: {
                            text_length: trimmed.length,
                            audio_playing_at_submit: audioIsPlaying,
                        },
                    });
                    // Clear our local response tracking — the next
                    // response.created event from the server will
                    // set it again for the new turn.
                    currentResponseRef.current = null;
                }
                sessionRef.current?.sendMessage?.(trimmed);
                pushDiag({
                    type: "user.sent_text",
                    detail: {
                        length: trimmed.length,
                        interrupted_active_response: isActiveResponse,
                    },
                });
            } catch (e) {
                console.error("[Qoryn] sendMessage failed:", e);
            }
        },
        [agentState, pushDiag],
    );

    return {
        agentState,
        connectionState,
        turns,
        error,
        heldOffline,
        sessionMeta,
        micMuted,
        audioMode,
        audioModeReason,
        sessionStartedAt,
        diagnostics,
        glossary,
        analyserRef,
        audioElRef,
        // Backend persistence (Phase 1C):
        persistenceState,
        persistedSessionId,
        persistenceError,
        connect,
        end,
        toggleMute,
        interrupt,
        sendText,
        clearTurns,
    };
}
